import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  DirectionsCar,
  People,
  LocalTaxi,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  Pending,
} from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import apiService from '../services/apiService';
import socketService from '../services/socketService';

const StatCard = ({ title, value, icon, color, trend, trendValue }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
            {value}
          </Typography>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {trend === 'up' ? (
                <TrendingUp sx={{ fontSize: 18, color: 'success.main' }} />
              ) : (
                <TrendingDown sx={{ fontSize: 18, color: 'error.main' }} />
              )}
              <Typography
                variant="caption"
                color={trend === 'up' ? 'success.main' : 'error.main'}
              >
                {trendValue}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: color + '20',
            p: 1.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 32, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalDrivers: 0,
    onlineDrivers: 0,
    totalPassengers: 0,
    totalRides: 0,
    activeRides: 0,
    revenueToday: 0,
    revenueMonth: 0,
    pendingVerifications: 0,
    pendingWithdrawals: 0,
  });
  const [ridesData, setRidesData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [vehicleTypeData, setVehicleTypeData] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        // Fetch stats
        const response = await apiService.getDashboardStats();

        if (response.success) {
          setStats({
            totalDrivers: response.data.totalDrivers || 0,
            onlineDrivers: response.data.onlineDrivers || 0,
            totalPassengers: response.data.totalPassengers || 0,
            totalRides: response.data.totalRides || 0,
            activeRides: response.data.activeRides || 0,
            revenueToday: response.data.revenueToday || 0,
            revenueMonth: response.data.revenueMonth || 0,
            pendingVerifications: response.data.pendingVerifications || 0,
            pendingWithdrawals: response.data.pendingWithdrawals || 0,
          });

          // Fetch chart data
          await loadChartData();
        } else {
          setError(response.message || 'Failed to load dashboard data');
        }
      } catch (err) {
        console.error('Dashboard error:', err);
        setError('Failed to load dashboard data. Please check if the backend server is running.');
      } finally {
        setLoading(false);
      }
    };

    const loadChartData = async () => {
      try {
        // Fetch rides chart data
        const ridesResponse = await apiService.api.get('/admin/dashboard/rides-chart');
        if (ridesResponse.success && ridesResponse.data) {
          const formattedRidesData = ridesResponse.data.map((item) => ({
            day: new Date(item.date).toLocaleDateString('en-US', { weekday: 'short' }),
            rides: item.count || 0,
            completed: item.completed || Math.floor(item.count * 0.85) || 0,
          }));
          setRidesData(formattedRidesData.length > 0 ? formattedRidesData : generateEmptyRidesData());
        } else {
          setRidesData(generateEmptyRidesData());
        }

        // Fetch revenue chart data
        const revenueResponse = await apiService.api.get('/admin/dashboard/revenue-chart');
        if (revenueResponse.success && revenueResponse.data) {
          const formattedRevenueData = revenueResponse.data.map((item) => ({
            month: item.month,
            revenue: item.revenue || 0,
          }));
          setRevenueData(formattedRevenueData.length > 0 ? formattedRevenueData : generateEmptyRevenueData());
        } else {
          setRevenueData(generateEmptyRevenueData());
        }

        // Fetch vehicle type distribution
        const vehicleResponse = await apiService.api.get('/admin/dashboard/vehicle-types');
        if (vehicleResponse.success && vehicleResponse.data) {
          setVehicleTypeData(vehicleResponse.data);
        } else {
          setVehicleTypeData([
            { name: 'Auto', value: 0, color: '#FFC107' },
            { name: 'Bike', value: 0, color: '#FF9800' },
            { name: 'Mini', value: 0, color: '#FF5722' },
            { name: 'Sedan', value: 0, color: '#F44336' },
            { name: 'SUV', value: 0, color: '#E91E63' },
          ]);
        }
      } catch (err) {
        console.error('Chart data error:', err);
        setRidesData(generateEmptyRidesData());
        setRevenueData(generateEmptyRevenueData());
        setVehicleTypeData([
          { name: 'Auto', value: 0, color: '#FFC107' },
          { name: 'Bike', value: 0, color: '#FF9800' },
          { name: 'Mini', value: 0, color: '#FF5722' },
          { name: 'Sedan', value: 0, color: '#F44336' },
          { name: 'SUV', value: 0, color: '#E91E63' },
        ]);
      }
    };

    const generateEmptyRidesData = () => {
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      return days.map((day) => ({
        day,
        rides: 0,
        completed: 0,
      }));
    };

    const generateEmptyRevenueData = () => {
      const months = [];
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push({
          month: date.toLocaleDateString('en-US', { month: 'short' }),
          revenue: 0,
        });
      }
      return months;
    };

    // Socket listener for real-time updates
    socketService.onDashboardUpdate((data) => {
      setStats((prev) => ({
        ...prev,
        ...data,
      }));
    });

    loadData();

    // Refresh data every 30 seconds
    const refreshInterval = setInterval(loadData, 30000);

    return () => {
      clearInterval(refreshInterval);
      socketService.off('admin:dashboard-update');
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Real-time overview of your taxi booking platform
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Drivers"
            value={stats.totalDrivers}
            icon={<DirectionsCar />}
            color="#FFC107"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Online Drivers"
            value={stats.onlineDrivers}
            icon={<DirectionsCar />}
            color="#4CAF50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Passengers"
            value={stats.totalPassengers}
            icon={<People />}
            color="#2196F3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Rides"
            value={stats.activeRides}
            icon={<LocalTaxi />}
            color="#FF9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Rides"
            value={stats.totalRides}
            icon={<LocalTaxi />}
            color="#9C27B0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Today's Revenue"
            value={`₹${stats.revenueToday.toLocaleString()}`}
            icon={<AttachMoney />}
            color="#4CAF50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Month's Revenue"
            value={`₹${stats.revenueMonth.toLocaleString()}`}
            icon={<AttachMoney />}
            color="#00BCD4"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Verifications"
            value={stats.pendingVerifications}
            icon={<Pending />}
            color="#F44336"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Rides Chart */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Rides Overview (Last 7 Days)
              </Typography>
              {ridesData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={ridesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="rides"
                      stroke="#FFC107"
                      strokeWidth={2}
                      name="Total Rides"
                    />
                    <Line
                      type="monotone"
                      dataKey="completed"
                      stroke="#4CAF50"
                      strokeWidth={2}
                      name="Completed"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No ride data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Vehicle Type Distribution */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Vehicle Type Distribution
              </Typography>
              {vehicleTypeData.some(v => v.value > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={vehicleTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${entry.value}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {vehicleTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No vehicle data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Chart */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Revenue Overview (Last 6 Months)
              </Typography>
              {revenueData.some(r => r.revenue > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="revenue" fill="#FFC107" name="Revenue (₹)" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No revenue data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;